import { useBoolean } from '@fluentui/react-hooks';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useEffect, useState } from 'react';
import useSessionStorageState from 'use-session-storage-state';
import { useGetFrontendConfiguration } from '../api/frontend-configuration/frontend-configuration';
import {
    useGetCalculationExecutionStatus,
    useGetCalculationExecutionStatuses,
    useScheduleTaxReturnCalculation,
} from '../api/report/report';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { IExecutionStatusStorage } from '../data-types/IExecutionStatusStorage';
import { GetJobStatusCodeValue, JobStatusCode } from '../data-types/jobStatusCode';
import { CalculationExecutionStatus, CalculationExecutionStatusCode } from '../model';
import { CALCULATION_REPORT_LIST_NAME } from '../utils/BrowserStorageKeys.constants';
import { CommandType } from '../utils/EventGrid.constants';
import { shouldInvalidateDependentQueries } from '../utils/TaxReturnUtils';
import { setConditionalTimeout } from '../utils/timerUtils';

export type OnReceiveJobStatusCallbackType = (res: CalculationExecutionStatus[]) => void;

export const getProcessingReportsStorageKey = (environmentId: string, userId: string): string => {
    return `${environmentId}-${userId}_${CALCULATION_REPORT_LIST_NAME}`;
};

export interface ICalculationExecutionStatusContextValues {
    taxReturnCalculationStatusList?: IExecutionStatusStorage[];
    isLoadingRecalculation: boolean;
    isErrorRecalculation: boolean;
    completeReportDependencies?: Record<number, QueryKey[]>;
    insertTaxReturnCalculationReport: (taxReturnCalculation: IExecutionStatusStorage) => void;
    updateTaxReturnCalculationReport: (taxReturnCalculation: IExecutionStatusStorage) => void;
    removeTaxReturnCalculationReport: (taxReturnKey: number) => void;
    isReportExecuting: (returnKey: number) => boolean;
    isReportPrinting: (returnKey: number) => boolean;
    getReportStatus: (returnKey: number) => IExecutionStatusStorage | undefined;
    updateTaxReturnProcessingStatus: (returnKey: number) => void;
    clearAllCompletedReports: () => void;
    rerunCalculationReport: (
        returnName: string | undefined | null,
        returnKey: number,
        taxReturnItemId: string,
        handleError?: boolean,
        onSuccess?: (data: any) => void
    ) => void;
    registerGlobalDependency: (triggerName: string, triggerFunction: (taxReturnItemKey: IExecutionStatusStorage) => void) => void;
    registerDependency: (taxReturnKey: number, queryKey: QueryKey) => void;
    getReportStatusByJobId: (jobId: number) => IExecutionStatusStorage | undefined;
    fetchAllCalculationExecutionStatuses: () => Promise<{ [key: string]: CalculationExecutionStatus } | undefined>;
    clearReportsWithoutJobid: () => void;
    updateTaxReturnUsingSignalR: (event: any, commandType: CommandType) => void;
    startPolling: (onReceiveJobStatusCallback: OnReceiveJobStatusCallbackType) => () => void;
}

export const TaxReturnCalculationContext = createContext<ICalculationExecutionStatusContextValues | null>(null);

export function TaxReturnCalculationProvider(props: { value?: ICalculationExecutionStatusContextValues; children?: React.ReactNode }) {
    const userClaims = useGetClaimsForCurrentUser();
    const queryClient = useQueryClient();
    const { mutateAsync } = useGetCalculationExecutionStatus();
    const {
        isLoading: isPostLoading,
        isError: isPostError,
        error,
        mutate: postScheduleReportCalculation,
    } = useScheduleTaxReturnCalculation();
    const { mutateAsync: getAllCalculationExecutionStatuses } = useGetCalculationExecutionStatuses();
    const [dataFromStorageReady, { setTrue: finishedLoadingFromStorage }] = useBoolean(false);

    const environmentId = userClaims?.data?.environmentId || '';
    const userId = userClaims?.data?.userId || '';
    const isProcessingReportStorageName = getProcessingReportsStorageKey(environmentId, userId);
    const [isProcessingReportsStorageValue, setIsProcessingReportsStorageValue] = useSessionStorageState(isProcessingReportStorageName, {
        defaultValue: [] as IExecutionStatusStorage[],
    });
    const [completeReportDependencies, setCompleteReportDependencies] = useState<Record<number, QueryKey[]>>([]);
    const [globalDependencyTriggers, setGlobalDependencyTriggers] = useState<
        Record<string, (taxReturnItemKey: IExecutionStatusStorage) => void>
    >({});
    const { data: frontendConfiguration } = useGetFrontendConfiguration();
    const defaultTaxReturnStatusPollingTime = frontendConfiguration?.taxReturnStatusPollingTimeMs || 30000;

    const updateTaxReturnProcessingStatus = (reportKey: number) => {
        const currentStatus = isProcessingReportsStorageValue.find((item) => item.taxReturnItemKey === reportKey);
        if (currentStatus?.jobId) {
            mutateAsync({ data: { jobIds: [currentStatus.jobId] } }).then((newStatusResponse) => {
                if (newStatusResponse?.length) {
                    updateTaxReturnCalculationReport({ ...currentStatus, ...newStatusResponse[0] });
                }
            });
        }
    };

    useEffect(() => {
        if (!dataFromStorageReady && isProcessingReportsStorageValue) {
            // The session storage library does not trigger an event when it finishes loading
            // And it takes a couple of re-renders, in which we try to clear the reports without jobid
            // Since the data did not load yet, nothing happens that's why we're waiting
            const timeoutId = setTimeout(() => {
                finishedLoadingFromStorage();
            }, 250);

            return () => clearTimeout(timeoutId);
        }
    }, [dataFromStorageReady, isProcessingReportsStorageValue, finishedLoadingFromStorage]);

    useEffect(() => {
        if (dataFromStorageReady) {
            clearReportsWithoutJobid();
        }
    }, [dataFromStorageReady]);

    const clearAllCompletedReports = () => {
        const filteredList = isProcessingReportsStorageValue?.filter(
            (item: IExecutionStatusStorage) => item.statusCode === CalculationExecutionStatusCode.Executing
        );
        setIsProcessingReportsStorageValue(filteredList || []);
    };

    const observedJobIds = isProcessingReportsStorageValue
        .filter((status) => status.jobId && status?.statusCode === CalculationExecutionStatusCode.Executing)
        .map((item) => item.jobId ?? 0);

    const updateAllObservedTaxReturnProcessingStatus = (onReceiveJobStatusCallback: OnReceiveJobStatusCallbackType) => {
        mutateAsync({ data: { jobIds: observedJobIds } }).then((response) => {
            updateAllTaxReturnCalculationReports(response);
            onReceiveJobStatusCallback(response);
        });
    };

    const startPolling = (onReceiveJobStatusCallback: OnReceiveJobStatusCallbackType) => {
        const cleanupFunction = setConditionalTimeout(
            () => updateAllObservedTaxReturnProcessingStatus(onReceiveJobStatusCallback),
            observedJobIds.length > 0,
            defaultTaxReturnStatusPollingTime
        );

        return cleanupFunction;
    };

    const updateAllTaxReturnCalculationReports = (taxReturnCalculations: CalculationExecutionStatus[]) => {
        taxReturnCalculations.forEach((taxReturnCalculation) => {
            updateTaxReturnCalculationReport({ ...taxReturnCalculation });
        });
    };

    const updateTaxReturnCalculationReport = (taxReturnCalculation: IExecutionStatusStorage) => {
        const existingItem = isProcessingReportsStorageValue.find((item) => item?.jobId === taxReturnCalculation.jobId);

        if (existingItem) {
            invalidateDependentQueries({ ...taxReturnCalculation, taxReturnItemKey: existingItem.taxReturnItemKey });
            setIsProcessingReportsStorageValue((prevList) =>
                prevList.map((item) =>
                    item.jobId === taxReturnCalculation.jobId
                        ? {
                              ...item,
                              statusCode: taxReturnCalculation.statusCode,
                              jobId: taxReturnCalculation.jobId,
                              commandType: taxReturnCalculation.commandType || existingItem.commandType,
                              batchProcessId: taxReturnCalculation.batchProcessId ?? existingItem.batchProcessId,
                              tocItemKey: taxReturnCalculation.tocItemKey ?? existingItem.tocItemKey,
                          }
                        : item
                )
            );
        } else {
            if (taxReturnCalculation.taxReturnItemKey) {
                const filteredExecutingJobs = isProcessingReportsStorageValue.filter(
                    (item) => item.taxReturnItemKey !== taxReturnCalculation.taxReturnItemKey
                );
                setIsProcessingReportsStorageValue([...filteredExecutingJobs, taxReturnCalculation]);
            }
        }
    };

    const invalidateDependentQueries = (taxReturnCalculation: IExecutionStatusStorage) => {
        const taxReturnItemKey = isProcessingReportsStorageValue.find(
            (item) => item.taxReturnItemKey === taxReturnCalculation.taxReturnItemKey
        )?.taxReturnItemKey;

        if (
            taxReturnItemKey !== undefined &&
            shouldInvalidateDependentQueries(taxReturnItemKey, taxReturnCalculation, isProcessingReportsStorageValue)
        ) {
            completeReportDependencies[taxReturnItemKey]?.forEach((key) => {
                queryClient.invalidateQueries(key);
            });
        }

        if (globalDependencyTriggers) {
            for (const triggerFunction of Object.values(globalDependencyTriggers)) {
                triggerFunction(taxReturnCalculation);
            }
        }
    };

    const insertTaxReturnCalculationReport = (taxReturnCalculation: IExecutionStatusStorage) => {
        if (
            !(isProcessingReportsStorageValue.filter((item) => item.taxReturnItemKey === taxReturnCalculation.taxReturnItemKey).length > 0)
        ) {
            setIsProcessingReportsStorageValue([...isProcessingReportsStorageValue, taxReturnCalculation]);
        }
    };

    const removeTaxReturnCalculationReport = (taxReturnKey: number) => {
        if (isProcessingReportsStorageValue.filter((item) => item.taxReturnItemKey === taxReturnKey).length > 0) {
            setIsProcessingReportsStorageValue([
                ...isProcessingReportsStorageValue.filter((item: IExecutionStatusStorage) => item.taxReturnItemKey !== taxReturnKey),
            ]);
        }
    };

    const isReportExecuting = (returnKey: number): boolean => {
        return isProcessingReportsStorageValue.some(
            (item: IExecutionStatusStorage) =>
                item?.taxReturnItemKey === returnKey &&
                item.statusCode === CalculationExecutionStatusCode.Executing &&
                item.commandType === CommandType.Execute
        );
    };

    const isReportPrinting = (returnKey: number): boolean => {
        return isProcessingReportsStorageValue.some(
            (item: IExecutionStatusStorage) =>
                item?.taxReturnItemKey === returnKey &&
                item.statusCode === CalculationExecutionStatusCode.Executing &&
                item.commandType === CommandType.Print
        );
    };

    const getReportStatus = (returnKey: number): IExecutionStatusStorage | undefined => {
        return isProcessingReportsStorageValue?.find((item: IExecutionStatusStorage) => item?.taxReturnItemKey === returnKey);
    };

    const updateTaxReturnUsingSignalR = (event: any, commandType: CommandType) => {
        const status = event.jobStatusCode === JobStatusCode.Unknown ? JobStatusCode.Executing : event.jobStatusCode;

        let taxReturnStatus = isProcessingReportsStorageValue?.find((item) => item.jobId === event.jobId);

        if (!taxReturnStatus) {
            taxReturnStatus = isProcessingReportsStorageValue?.find((item) => event.message.includes(item.taxReturnItemName));
        }

        updateTaxReturnCalculationReport({
            ...taxReturnStatus,
            statusCode: GetJobStatusCodeValue(status) as CalculationExecutionStatusCode,
            jobId: event?.jobId,
            commandType,
        });
    };

    const rerunCalculationReport = async (
        returnName: string | undefined | null,
        returnKey: number,
        taxReturnItemId: string,
        handleError?: boolean,
        onSuccess?: (data: any) => void
    ) => {
        try {
            const taxReturn: IExecutionStatusStorage = {
                taxReturnItemName: returnName,
                taxReturnItemKey: returnKey,
                jobId: undefined,
                statusCode: CalculationExecutionStatusCode.Executing,
                commandType: CommandType.Execute,
            };

            updateTaxReturnCalculationReport(taxReturn);

            postScheduleReportCalculation(
                {
                    data: { reportId: taxReturnItemId },
                },
                {
                    onError: () => {
                        removeTaxReturnCalculationReport(returnKey);
                        if (handleError && isPostError) {
                            let errorMessage;
                            if (error && error.message) {
                                errorMessage = error?.request?.response;
                            }
                            alert(errorMessage);
                        }
                    },
                    onSuccess: (data: any) => {
                        if (onSuccess) {
                            onSuccess(data);
                        }
                        updateTaxReturnCalculationReport({ ...taxReturn, jobId: data.jobId });
                    },
                }
            );
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };

    const registerGlobalDependency = (triggerName: string, triggerFunction: (executionStatus: IExecutionStatusStorage) => void): void => {
        setGlobalDependencyTriggers((prevState) => {
            return {
                ...prevState,
                [triggerName]: triggerFunction,
            };
        });
    };

    const registerDependency = (taxReturnKey: number, queryKey: QueryKey): void => {
        const existingRecordWithKey = completeReportDependencies[taxReturnKey];

        if (!existingRecordWithKey) {
            setCompleteReportDependencies((prevState) => {
                return {
                    ...prevState,
                    [taxReturnKey]: [queryKey],
                };
            });
        } else {
            if (!existingRecordWithKey.some((key) => areQueriesEqual(key, queryKey))) {
                setCompleteReportDependencies((prevState) => {
                    return {
                        ...prevState,
                        [taxReturnKey]: [...prevState[taxReturnKey], queryKey],
                    };
                });
            }
        }
    };

    const areQueriesEqual = (queryKey1: QueryKey, queryKey2: QueryKey): boolean => {
        return JSON.stringify(queryKey1) === JSON.stringify(queryKey2);
    };

    const getReportStatusByJobId = (jobId: number): IExecutionStatusStorage | undefined => {
        return isProcessingReportsStorageValue?.find((item: IExecutionStatusStorage) => item?.jobId === jobId);
    };

    const fetchAllCalculationExecutionStatuses = async () => {
        const latestCalculationStatus = await getAllCalculationExecutionStatuses();

        if (latestCalculationStatus?.statuses) {
            return latestCalculationStatus.statuses;
        }

        return undefined;
    };

    const clearReportsWithoutJobid = () => {
        const filteredList = isProcessingReportsStorageValue?.filter((item: IExecutionStatusStorage) => item.jobId !== undefined);
        setIsProcessingReportsStorageValue(filteredList || []);
    };

    return (
        <TaxReturnCalculationContext.Provider
            value={
                props.value ?? {
                    taxReturnCalculationStatusList: isProcessingReportsStorageValue,
                    isLoadingRecalculation: isPostLoading,
                    isErrorRecalculation: isPostError,
                    completeReportDependencies,
                    insertTaxReturnCalculationReport,
                    isReportExecuting,
                    isReportPrinting,
                    getReportStatus,
                    updateTaxReturnProcessingStatus,
                    removeTaxReturnCalculationReport,
                    updateTaxReturnCalculationReport,
                    clearAllCompletedReports,
                    rerunCalculationReport,
                    registerGlobalDependency,
                    registerDependency,
                    getReportStatusByJobId,
                    fetchAllCalculationExecutionStatuses,
                    clearReportsWithoutJobid,
                    updateTaxReturnUsingSignalR,
                    startPolling,
                }
            }
        >
            {props.children}
        </TaxReturnCalculationContext.Provider>
    );
}
